<script setup lang="ts">
  import * as changeCase from 'change-case';
  import pluralize from 'pluralize';
  import { computed } from 'vue';

  import type { Invoice } from '@stuut/api/models';
  import { InvoiceStatus, InvoiceStatusLabels } from '@stuut/api/models';
  import { Badge } from '@ui/components/badge';
  import type { BadgeVariants } from '@ui/components/badge';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';

  // Complete InvoiceStatusBadge with business logic and presentation
  // Used by both dashboard and payments apps
  interface InvoiceBadgeProps {
    invoice?: Invoice;
    label?: string;
    noLabel?: boolean;
    size?: BadgeVariants['size'];
    status?: InvoiceStatus;
    variant?: BadgeVariants['variant'];
  }

  const props = defineProps<InvoiceBadgeProps>();

  const displayLabel = computed(() => {
    if (props.label) return props.label;

    if (props.status && InvoiceStatusLabels[props.status]) {
      return InvoiceStatusLabels[props.status];
    }

    if (!props.invoice) return InvoiceStatusLabels[InvoiceStatus.UNKNOWN];

    if (props.invoice.status === InvoiceStatus.PROCESSING) {
      return InvoiceStatusLabels[InvoiceStatus.PROCESSING];
    }

    if (props.invoice.pending_amount_remaining === 0) {
      return InvoiceStatusLabels[InvoiceStatus.PAID];
    }

    if (!props.invoice.isOpen || !props.invoice.due_date) {
      return changeCase.capitalCase(props.invoice.status);
    }

    if (props.invoice.isOverdue) {
      const daysOverdue = props.invoice.daysOverdue;
      return daysOverdue ? `${daysOverdue} ${pluralize('day', daysOverdue)} Overdue` : 'Overdue';
    }

    const daysUntilDue = props.invoice.daysUntilDue;
    return `Due in ${daysUntilDue} ${pluralize('day', daysUntilDue)}`;
  });

  const computedStatus = computed(() => {
    return props.status ?? props.invoice?.status ?? InvoiceStatus.UNKNOWN;
  });

  const computedBadgeVariant = (status: InvoiceStatus): BadgeVariants['variant'] => {
    if (props.invoice?.pending_amount_remaining === 0) {
      return 'success';
    }

    switch (status) {
      case InvoiceStatus.PAID:
        return 'success';
      case InvoiceStatus.OVERDUE:
        return 'destructive';
      case InvoiceStatus.OPEN:
        return props.invoice?.isOverdue ? 'destructive' : 'open';
      case InvoiceStatus.PROCESSING:
      case InvoiceStatus.PARTIALLY_PAID:
        return 'warning';
      case InvoiceStatus.PENDING_SYNC:
      case InvoiceStatus.UNCOLLECTIBLE:
      case InvoiceStatus.VOID:
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const badgeVariant = computed(() => props.variant ?? computedBadgeVariant(computedStatus.value));
</script>

<template>
  <template v-if="noLabel">
    <Tooltip>
      <TooltipTrigger as-child>
        <Badge class="size-2! p-0 rounded-none" :variant="badgeVariant"> </Badge>
      </TooltipTrigger>
      <TooltipContent>
        <span class="truncate" :title="displayLabel">{{ displayLabel }}</span>
      </TooltipContent>
    </Tooltip>
  </template>

  <Badge v-else :size="size || 'sm'" :variant="badgeVariant">
    <span class="truncate" :title="displayLabel">{{ displayLabel }}</span>
  </Badge>
</template>

<style scoped></style>
